/**
 * Display a geolocation-based popup suggesting an alternative domain 
 * if the user's country differs from the current store's country.
 */
class GeolocationPopup extends HTMLElement {
    constructor() {
        super();
        this.geoObserver;
        this.suggestedCountry;

        this.COOKIE_NAME = "geo_popup_dismissed";
        this.COOKIE_MAX_AGE = 24 * 60 * 60; // 24 hours
    }

    /** 
     * Initialise dialog elements, fetch current country code, 
     * and trigger geolocation handling if no dismissal cookie is set.
    */
    connectedCallback() {
        this.dialog = this.querySelector("dialog");
        this.continueButton = this.querySelector(".geolocation-continue");
        this.closeButton = this.querySelector(".geolocation-close");

        this.currentCountryCode = this.dataset.currentCountryCode;

        if (this.getCookie(this.COOKIE_NAME)) return;

        this.waitForGeo();
        this.setUpEventListeners();
    }

    /** 
     * Clean up observers and close dialog when element is removed from DOM.
    */
    disconnectedCallback() {
        if (this.dialog?.open) this.dialog.close();
        if (this.geoObserver) this.geoObserver.disconnect();
    }

    /** 
     * Attach click event listeners to the close and continue buttons.
    */
    setUpEventListeners() {
        this.closeButton.addEventListener("click", () => this.handleClose());
        this.continueButton.addEventListener("click", () =>
            this.handleContinue()
        );
    }

    /** 
     * Redirect user to suggested country's domain if available.
    */
    handleContinue() {
        if (!this.suggestedCountry) return;

        window.location.href = this.suggestedCountry.domain;
    }

    /** 
     * Close dialog and set a cookie to prevent future popups.
    */
    handleClose() {
        this.dialog.close()
        this.setCookie(this.COOKIE_NAME, true, this.COOKIE_MAX_AGE);
    }

    /** 
     * Wait for global geolocation variables to become available,
     * using a MutationObserver if needed.
    */
    waitForGeo() {
        if (window.__GEO__ && window.__GEO_MARKETS__) {
            this.handleGeo();
        } else {
            this.geoObserver = new MutationObserver(() => {
                if (window.__GEO__ && window.__GEO_MARKETS__) {
                    this.handleGeo();
                }
            });

            this.geoObserver.observe(document.documentElement, {
                attributes: true,
            });
        }
    }

    /** 
     * Process geolocation data and display popup if user's country differs 
     * from the current store country.
    */
    handleGeo() {
        if (this.geoObserver) {
            this.geoObserver.disconnect();
        }

        const userCountryCode = window.__GEO__.countryCode;

        if (userCountryCode && userCountryCode !== this.currentCountryCode) {
            this.suggestedCountry = window.__GEO_MARKETS__[userCountryCode];
            if (!this.suggestedCountry) return;

            this.populateLabels();
            this.populateFlag();
            this.dialog.showModal();
            this.dialog.classList.add('show');
        }
    }

    /** 
     * Populate all label elements inside the popup with the suggested country's name.
    */
    populateLabels() {
        const userCountryLabel = this.suggestedCountry.label;
        const labelsToPopulate = this.querySelectorAll(
            "[data-geolocation-country]"
        );
        labelsToPopulate.forEach((element) => {
            element.textContent = userCountryLabel;
        });
    }

    /** 
     * Update the popup's flag image to match the suggested country.
    */
    populateFlag() {
        const userCountryFlag = this.suggestedCountry.flag;
        const flagToPopulate = this.querySelector(".geolocation-flag");
        flagToPopulate.src = userCountryFlag;
    }

    /** 
     * Set a cookie with the given name, value, and max-age in seconds.
    */
    setCookie(name, value, maxAgeSeconds) {
        const str = encodeURIComponent(JSON.stringify(value));
        document.cookie = `${name}=${str}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
    }

    /** 
     * Retrieve the value of a cookie by name, or null if not set.
    */
    getCookie(name) {
        const cookieValue = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))?.split('=')[1];
        return cookieValue ?? null;
    }
}

if (!customElements.get("geolocation-popup")) {
    customElements.define("geolocation-popup", GeolocationPopup);
}
